import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Search, ArrowRight, ChevronDown, ChevronRight,
  Home, Key, Building2, Briefcase, Globe2, TrendingUp,
  ShieldCheck, FileText, Gift, Star, Crown, Wallet, Loader2, Copy, Check,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AI Network slide-out panel.
 *
 * Left-side companion to the right-side "How It Works" tab. It's a
 * positive trust-and-discovery hub — no competitor name-calling.
 *
 * Surfaces every value prop in one panel:
 *   • Pulse Verified investors badge
 *   • Green-marked premium property pipeline
 *   • Pulse Invest Report (PDF) CTA
 *   • Live AI match counts per region
 *   • 25-50% referral bonus
 *   • Affiliate programme entry
 *   • Pro dropdown: top Estate Agents / Solicitors / Brokers per city
 *
 * Backed by GET /api/sales/ai-network-stats and GET /api/pro-leads.
 */

const ROLES = [
  { id: 'buyer',        label: 'Buyer',     icon: Home,        hint: 'e.g. "3-bed in Manchester under £350k with garden"',                  cta: 'Find my home',         routeFor: (q) => `/properties/uk?q=${encodeURIComponent(q || '')}&role=buyer` },
  { id: 'tenant',       label: 'Tenant',    icon: Key,         hint: 'e.g. "Pet-friendly 2-bed in Leeds under £1,200/mo"',                  cta: 'Find my rental',       routeFor: (q) => `/properties/uk?q=${encodeURIComponent(q || '')}&type=rent&role=tenant` },
  { id: 'investor',     label: 'Investor',  icon: TrendingUp,  hint: 'e.g. "Best 8%+ yield Liverpool BTL postcodes"',                      cta: 'See yield map',        routeFor: (q) => `/properties/uk?q=${encodeURIComponent(q || '')}&role=investor` },
  { id: 'nri',          label: 'NRI',       icon: Globe2,      hint: 'e.g. "Mohali Sector 70 · 1-kanal NRI plots"',                        cta: 'Match me in India',    routeFor: (q) => `/properties/india?q=${encodeURIComponent(q || '')}&role=nri` },
  { id: 'seller',       label: 'Seller',    icon: Building2,   hint: 'List your property — AI valuation + verified-buyer matching',        cta: 'List my property',     routeFor: () => `/seller` },
  { id: 'professional', label: 'Pro',       icon: Briefcase,   hint: 'Agents · Surveyors · Solicitors · Mortgage Brokers',                  cta: 'Join Verified Pros',   routeFor: () => `/agents` },
];

const PRO_CATEGORIES = [
  { id: 'estate agents',         label: 'Top Estate Agents',     icon: Building2, route: '/find-a-professional?category=estate%20agent' },
  { id: 'real estate solicitors', label: 'Top Solicitors',       icon: ShieldCheck, route: '/find-a-professional?category=solicitor' },
  { id: 'mortgage brokers',      label: 'Top Mortgage Brokers',   icon: Wallet, route: '/find-a-professional?category=mortgage%20broker' },
];

const COUNTRY_PRETTY = { UK: 'United Kingdom', IN: 'India', CA: 'Canada', OTHER: 'Other' };

const AINetworkTab = ({ dataTestId = 'ai-network-tab' }) => {
  const [open, setOpen] = useState(false);
  const [roleId, setRoleId] = useState('buyer');
  const [query, setQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [proCity, setProCity] = useState('London');
  const [proCategory, setProCategory] = useState('estate agents');
  const [topPros, setTopPros] = useState(null);
  const [loadingPros, setLoadingPros] = useState(false);
  const [affiliate, setAffiliate] = useState(null); // {name, referral_code, total_earned, pending, total_paid} when logged in
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const role = ROLES.find(r => r.id === roleId) || ROLES[0];

  // Lazy-load the visitor's affiliate identity the first time the
  // panel opens. We use the existing /api/affiliate/me endpoint which
  // accepts the bearer token from localStorage. Silently 401s for
  // logged-out visitors and the panel falls back to the generic
  // invite banner — no error toast, no broken UI.
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API_URL}/api/affiliate/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setAffiliate(r.data))
      .catch(() => setAffiliate(null));
  }, [open]);

  const referralUrl = affiliate?.share_link || (
    affiliate?.referral_code
      ? `https://shopperden.com/r/${affiliate.referral_code}`
      : null
  );

  // Aggregate the per-currency `balance` map into a single GBP-ish
  // headline for the strip. The Affiliate Dashboard shows the full
  // per-currency breakdown.
  const lifetimeEarned = affiliate?.lifetime_earned ?? 0;
  const pendingAcrossCcy = Object.values(affiliate?.balance || {})
    .reduce((sum, v) => sum + (Number(v?.pending) || 0), 0);
  const paidAcrossCcy = Object.values(affiliate?.balance || {})
    .reduce((sum, v) => sum + (Number(v?.paid) || 0), 0);

  const copyReferral = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success('Referral link copied — paste anywhere');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Copy failed — long-press to copy manually');
    }
  };

  // Lazy-load stats the first time the panel opens
  useEffect(() => {
    if (!open || stats) return;
    axios.get(`${API_URL}/api/sales/ai-network-stats`)
      .then(r => setStats(r.data))
      .catch(() => setStats({ by_country: {}, pros_by_category: {}, totals: { properties: 0, pros: 0 } }));
  }, [open, stats]);

  // Fetch top pros when the Pro tab is active or category/city changes
  useEffect(() => {
    if (!open || roleId !== 'professional') return;
    let cancelled = false;
    setLoadingPros(true);
    axios.get(`${API_URL}/api/pro-leads`, { params: { city: proCity, category: proCategory, limit: 5 } })
      .then(r => { if (!cancelled) setTopPros(r.data?.items || []); })
      .catch(() => { if (!cancelled) setTopPros([]); })
      .finally(() => { if (!cancelled) setLoadingPros(false); });
    return () => { cancelled = true; };
  }, [open, roleId, proCity, proCategory]);

  const submit = (e) => {
    e?.preventDefault?.();
    const path = role.routeFor(query.trim());
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* Floating left-side pill — mirrors the right "How It Works" tab.
          Uses the same writing-mode pattern (no rotate) so iPad Safari
          renders it identically to Chrome/iOS — earlier rotate(180deg)
          flipped the glyphs upside-down on iPadOS. */}
      <button
        onClick={() => setOpen(true)}
        className="fixed left-0 top-1/2 -translate-y-1/2 z-[60] bg-[#0F172A] text-white px-3 py-6 shadow-xl hover:bg-[#1e293b] transition-all duration-300 hover:px-4 group"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', borderRadius: '0 8px 8px 0' }}
        data-testid={dataTestId}
        aria-label="Open AI Network"
      >
        <span className="text-xs font-semibold tracking-[0.15em] uppercase flex items-center gap-2">
          <Sparkles className="h-4 w-4 rotate-90 group-hover:text-[#E07A3A] transition-colors" />
          AI Network
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70]" data-testid={`${dataTestId}-overlay`}>
          <div
            className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            style={{ animation: 'fadeIn 0.3s ease-out' }}
          />
          <div
            className="absolute left-0 top-0 h-full w-full max-w-2xl bg-[#0F172A] overflow-y-auto shadow-2xl"
            style={{ animation: 'slideInLeft 0.35s cubic-bezier(0.4, 0, 0.2, 1)' }}
            data-testid={`${dataTestId}-panel`}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0F172A]/95 backdrop-blur-md">
              <div>
                <span className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-semibold">AI Search & Match</span>
                <h2 className="font-['Playfair_Display'] text-xl text-white font-semibold leading-tight">The AI Real Estate Network</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-10 h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors rounded"
                data-testid={`${dataTestId}-close`}
                aria-label="Close"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="px-6 py-8">
              {/* Live region counters */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-7" data-testid={`${dataTestId}-counters`}>
                {['UK', 'IN', 'CA'].map(cc => (
                  <div key={cc} className="bg-white/5 border border-white/10 rounded p-3 text-center">
                    <p className="text-[9px] tracking-[0.2em] uppercase text-white/50 font-bold">{COUNTRY_PRETTY[cc]}</p>
                    <p className="text-xl font-bold text-white mt-0.5 leading-none">
                      {stats?.by_country?.[cc]?.toLocaleString() ?? '—'}
                    </p>
                    <p className="text-[10px] text-white/40 mt-1">AI-matched</p>
                  </div>
                ))}
                <div className="bg-[#1F3D2B]/40 border border-[#14532D]/60 rounded p-3 text-center">
                  <p className="text-[9px] tracking-[0.2em] uppercase text-emerald-300 font-bold">Verified Pros</p>
                  <p className="text-xl font-bold text-white mt-0.5 leading-none">{stats?.totals?.pros?.toLocaleString() ?? '—'}</p>
                  <p className="text-[10px] text-white/40 mt-1">Pulse-verified</p>
                </div>
              </div>

              {/* Role tabs */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5" data-testid={`${dataTestId}-role-tabs`}>
                {ROLES.map(r => {
                  const Icon = r.icon;
                  const active = r.id === roleId;
                  return (
                    <button
                      key={r.id}
                      onClick={() => setRoleId(r.id)}
                      className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded border transition-all ${
                        active
                          ? 'bg-[#E07A3A] border-[#E07A3A] text-white shadow-lg'
                          : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                      data-testid={`${dataTestId}-role-${r.id}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-[10px] uppercase tracking-wider font-bold">{r.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Big AI search bar */}
              <form onSubmit={submit} className="bg-white rounded-md shadow-2xl overflow-hidden mb-7">
                <div className="flex items-stretch">
                  <div className="flex items-center px-4 text-slate-400 border-r border-slate-200">
                    <Search className="h-5 w-5" />
                  </div>
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={role.hint}
                    className="flex-1 px-3 py-4 text-sm text-slate-800 placeholder-slate-400 focus:outline-none min-w-0"
                    data-testid={`${dataTestId}-query`}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="bg-[#0F172A] hover:bg-[#1e293b] text-white px-5 sm:px-7 text-xs tracking-widest uppercase font-bold whitespace-nowrap flex items-center gap-2 transition-colors"
                    data-testid={`${dataTestId}-submit`}
                  >
                    {role.cta} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </form>

              {/* Pro top-list — only when Pro tab is active */}
              {roleId === 'professional' && (
                <div className="mb-7 space-y-4" data-testid={`${dataTestId}-pro-directory`}>
                  {/* Honest affiliate invite banner — personalised for
                      logged-in users, generic invite for anonymous. */}
                  {affiliate?.referral_code ? (
                    <div
                      className="bg-gradient-to-br from-[#E07A3A] to-[#9A3412] rounded p-5 shadow-lg"
                      data-testid={`${dataTestId}-affiliate-personalised`}
                    >
                      <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/90 mb-1.5 flex items-center gap-1.5">
                        <Gift className="h-3.5 w-3.5" />
                        {affiliate.first_name ? `Welcome back, ${affiliate.first_name}` : 'Welcome back'} · your link earns automatically · {affiliate.commission_pct ?? 15}% per sale
                      </p>
                      <p className="font-['Playfair_Display'] text-lg text-white leading-snug">
                        Share your link. The next paid checkout pays you instantly.
                      </p>

                      {/* Live earnings strip */}
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="bg-black/25 rounded px-2 py-1.5">
                          <p className="text-[9px] tracking-[0.15em] uppercase text-white/60 font-bold">Lifetime</p>
                          <p className="text-sm font-bold text-white" data-testid={`${dataTestId}-affiliate-earned`}>
                            £{lifetimeEarned.toFixed(2)}
                          </p>
                        </div>
                        <div className="bg-black/25 rounded px-2 py-1.5">
                          <p className="text-[9px] tracking-[0.15em] uppercase text-white/60 font-bold">Pending</p>
                          <p className="text-sm font-bold text-white" data-testid={`${dataTestId}-affiliate-pending`}>
                            £{pendingAcrossCcy.toFixed(2)}
                          </p>
                        </div>
                        <div className="bg-black/25 rounded px-2 py-1.5">
                          <p className="text-[9px] tracking-[0.15em] uppercase text-white/60 font-bold">Paid out</p>
                          <p className="text-sm font-bold text-white" data-testid={`${dataTestId}-affiliate-paid`}>
                            £{paidAcrossCcy.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {/* Copy-able referral link */}
                      <div className="flex items-stretch gap-2 mt-3">
                        <div className="flex-1 bg-black/30 border border-white/15 rounded px-3 py-2 text-xs font-mono text-white/95 truncate" data-testid={`${dataTestId}-affiliate-link`}>
                          {referralUrl}
                        </div>
                        <button
                          onClick={copyReferral}
                          className="bg-white text-[#9A3412] hover:bg-white/90 text-[10px] uppercase tracking-wider font-bold px-3 py-2 rounded inline-flex items-center gap-1.5 whitespace-nowrap"
                          data-testid={`${dataTestId}-affiliate-copy`}
                        >
                          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          {copied ? 'Copied' : 'Copy'}
                        </button>
                      </div>

                      <a
                        href="/affiliate"
                        className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-black/25 hover:bg-black/40 px-3 py-1.5 rounded"
                        data-testid={`${dataTestId}-affiliate-open-dashboard`}
                      >
                        Open Affiliate Dashboard <ChevronRight className="h-3 w-3" />
                      </a>
                    </div>
                  ) : (
                    <a
                      href="/affiliate"
                      className="block bg-gradient-to-br from-[#E07A3A] to-[#9A3412] hover:brightness-110 rounded p-5 transition shadow-lg"
                      data-testid={`${dataTestId}-affiliate-invite`}
                    >
                      <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/90 mb-1.5 flex items-center gap-1.5">
                        <Gift className="h-3.5 w-3.5" /> Earn while you share · free to join
                      </p>
                      <p className="font-['Playfair_Display'] text-lg text-white leading-snug">
                        SHOPPERDEN.COM invites you to earn money by joining our free Affiliate Programme.
                      </p>
                      <p className="text-sm text-white/85 mt-2 leading-relaxed">
                        Start sharing your link. Once a payment is made successfully through it, your commission
                        lands in your Stripe account <b>automatically</b> — no chasing, no waiting.
                      </p>
                      <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-black/25 px-3 py-1.5 rounded">
                        Open Affiliate Dashboard <ChevronRight className="h-3 w-3" />
                      </div>
                    </a>
                  )}

                  <div className="bg-white/5 border border-white/10 rounded p-4">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F0A875]">Top-rated verified pros</p>
                      <input
                        type="text"
                        value={proCity}
                        onChange={(e) => setProCity(e.target.value)}
                        placeholder="City (e.g. London, Mumbai)"
                        className="text-xs text-white bg-white/10 border border-white/20 rounded px-3 py-1.5 focus:outline-none focus:border-[#E07A3A] w-44"
                        data-testid={`${dataTestId}-pro-city`}
                      />
                    </div>
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {PRO_CATEGORIES.map(c => {
                        const Icon = c.icon;
                        const active = c.id === proCategory;
                        return (
                          <button
                            key={c.id}
                            onClick={() => setProCategory(c.id)}
                            className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border transition-all ${
                              active ? 'bg-[#E07A3A] border-[#E07A3A] text-white' : 'border-white/15 text-white/70 hover:bg-white/10'
                            }`}
                            data-testid={`${dataTestId}-pro-cat-${c.id.replace(/\s/g, '-')}`}
                          >
                            <Icon className="h-3 w-3" />
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="space-y-1.5 mt-3" data-testid={`${dataTestId}-pro-list`}>
                      {loadingPros ? (
                        <div className="flex items-center justify-center py-5 text-white/40">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : (topPros && topPros.length > 0) ? (
                        topPros.map((p, i) => (
                          <a
                            key={p.place_id || i}
                            href={`/pros/${(p.city || proCity).toLowerCase().replace(/\s/g, '-')}`}
                            className="flex items-center gap-3 py-2 px-2 rounded hover:bg-white/10 transition-colors group"
                            data-testid={`${dataTestId}-pro-${i}`}
                          >
                            <div className="w-7 h-7 rounded bg-[#0F172A] flex items-center justify-center text-[10px] font-bold text-[#F0A875] border border-white/10">#{i + 1}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate group-hover:text-[#F0A875]">{p.name}</p>
                              <p className="text-[10px] text-white/40 flex items-center gap-1.5">
                                <Star className="h-2.5 w-2.5 text-[#F0A875] fill-[#F0A875]" />
                                {p.rating || '—'} · {p.review_count || 0} reviews · {p.city}
                              </p>
                            </div>
                            <ChevronRight className="h-3.5 w-3.5 text-white/30 group-hover:text-white" />
                          </a>
                        ))
                      ) : (
                        <p className="text-xs text-white/50 py-3 text-center">Verified-pro directory is loading for "{proCity}" — try London, Manchester, Mumbai or Toronto.</p>
                      )}
                    </div>
                    <a href={PRO_CATEGORIES.find(c => c.id === proCategory)?.route || '/find-a-professional'} className="block mt-3 text-center text-[11px] text-[#F0A875] hover:text-white uppercase tracking-wider font-bold py-2 border-t border-white/10" data-testid={`${dataTestId}-pro-view-all`}>
                      View all verified pros →
                    </a>
                  </div>
                </div>
              )}

              {/* Trust & monetisation cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a
                  href="/free-valuation"
                  className="block bg-white/5 hover:bg-white/10 border border-emerald-700/40 rounded p-4 transition-colors"
                  data-testid={`${dataTestId}-pulse-verified`}
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold mb-1 flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Pulse-Verified Investors</p>
                  <p className="text-sm text-white leading-snug">Independent valuation · provable cash position · green-marked listings only.</p>
                </a>
                <a
                  href="/properties/uk?premium=1"
                  className="block bg-white/5 hover:bg-white/10 border border-emerald-700/40 rounded p-4 transition-colors"
                  data-testid={`${dataTestId}-green-marked`}
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold mb-1 flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500"></span>Green-Marked Property</p>
                  <p className="text-sm text-white leading-snug">Vendor-verified, fast-track ID, AI-valued — the badge buyers chase.</p>
                </a>
                <a
                  href="/free-valuation"
                  className="block bg-white/5 hover:bg-white/10 border border-white/10 rounded p-4 transition-colors"
                  data-testid={`${dataTestId}-pulse-pdf`}
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#F0A875] font-bold mb-1 flex items-center gap-1"><FileText className="h-3 w-3" />Pulse Invest Report (PDF)</p>
                  <p className="text-sm text-white leading-snug">28-page market intelligence · prices, yields, RERA flags · £29/₹2,499/C$39.</p>
                </a>
                <a
                  href="/affiliate"
                  className="block bg-white/5 hover:bg-white/10 border border-[#E07A3A]/40 rounded p-4 transition-colors"
                  data-testid={`${dataTestId}-affiliate`}
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#F0A875] font-bold mb-1 flex items-center gap-1"><Gift className="h-3 w-3" />Affiliate Programme</p>
                  <p className="text-sm text-white leading-snug">Up to 50% referral on city subscriptions · instant Stripe payouts.</p>
                </a>
                <a
                  href="/for/agents"
                  className="block bg-white/5 hover:bg-white/10 border border-white/10 rounded p-4 transition-colors"
                  data-testid={`${dataTestId}-subscription`}
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#F0A875] font-bold mb-1 flex items-center gap-1"><Crown className="h-3 w-3" />City Subscription</p>
                  <p className="text-sm text-white leading-snug">Per-region access · AI buyer routing · unlimited bulk listings.</p>
                </a>
                <a
                  href="/tenant-guide"
                  className="block bg-white/5 hover:bg-white/10 border border-white/10 rounded p-4 transition-colors"
                  data-testid={`${dataTestId}-tenant-guide`}
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#F0A875] font-bold mb-1 flex items-center gap-1"><Key className="h-3 w-3" />Tenant Guide</p>
                  <p className="text-sm text-white leading-snug">Deposit rules · referencing · AST templates · UK·IN·CA.</p>
                </a>
              </div>

              <div className="flex items-center justify-center gap-2 mt-8 text-white/40 text-[11px]">
                <ChevronDown className="h-3 w-3 -rotate-90" />
                <span>RERA & MEPC tagged · GDPR compliant · 360° tours · AI valuation</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }
      `}</style>
    </>
  );
};

export default AINetworkTab;
